import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ApiError, ApiResponse } from "@/api/rest/core/types";
import { AdminAccount, AdminDevelopment } from "@/api/rest/models/admin";
import {
  addAccountToBuilding,
  deleteBuilding,
  getBuildingDetails,
  listAccounts,
  listBuildings,
  listDevelopments,
  removeAccountFromBuilding,
  updateBuildingAddress,
  updateBuildingDevelopment,
} from "@/api/rest/services/adminService";
import Button from "@/components/common/Button";
import ConfirmationDialog from "@/components/common/ConfirmationDialog";
import Field from "@/components/common/Field";
import Input from "@/components/common/Input";
import Spinner from "@/components/common/Spinner";

type SortColumn = "id" | "street" | "city" | "postcode";
type SortDirection = "asc" | "desc";

export default function BuildingsTab({
  selectedBuildingId,
  onSelectedBuildingIdChange,
  onSelectAccount,
  onSelectDevelopment,
}: {
  selectedBuildingId: number | null;
  onSelectedBuildingIdChange: (buildingId: number | null) => void;
  onSelectAccount: (accountId: number) => void;
  onSelectDevelopment: (developmentId: number) => void;
}) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showChangeDevelopmentModal, setShowChangeDevelopmentModal] = useState(false);
  const [showAddOwnerModal, setShowAddOwnerModal] = useState(false);
  const [showEditAddressModal, setShowEditAddressModal] = useState(false);
  const [showDeleteBuildingModal, setShowDeleteBuildingModal] = useState(false);
  const [deleteConfirmationValue, setDeleteConfirmationValue] = useState("");
  const [pendingRemoveOwner, setPendingRemoveOwner] = useState<{ accountId: number; name: string } | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn>("id");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const selectedRowRef = useRef<HTMLTableRowElement>(null);

  const search = searchParams.get("buildingSearch") ?? "";
  const setSearch = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set("buildingSearch", value);
    } else {
      newParams.delete("buildingSearch");
    }
    setSearchParams(newParams, { replace: true });
  };

  const buildingsQuery = useInfiniteQuery({
    queryKey: ["admin-buildings", search, sortColumn, sortDirection],
    queryFn: ({ pageParam = 0 }) => listBuildings(search || undefined, pageParam, 20, sortColumn, sortDirection),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const result = lastPage.result;
      if (!result || result.page >= result.totalPages - 1) {
        return undefined;
      }
      return result.page + 1;
    },
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && buildingsQuery.hasNextPage && !buildingsQuery.isFetchingNextPage) {
          buildingsQuery.fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingsQuery.hasNextPage, buildingsQuery.isFetchingNextPage, buildingsQuery.fetchNextPage]);

  const buildingDetailsQuery = useQuery({
    queryKey: ["admin-building", selectedBuildingId],
    queryFn: () => getBuildingDetails(selectedBuildingId!),
    enabled: selectedBuildingId !== null,
  });

  const updateDevelopmentMutation = useMutation({
    mutationFn: async ({ buildingId, developmentId }: { buildingId: number; developmentId: number | null }) => {
      unwrapApiResponse(await updateBuildingDevelopment(buildingId, { developmentId }));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-building", selectedBuildingId] });
      queryClient.invalidateQueries({ queryKey: ["admin-buildings"] });
      setShowChangeDevelopmentModal(false);
    },
  });

  const addOwnerMutation = useMutation({
    mutationFn: async ({ buildingId, accountId }: { buildingId: number; accountId: number }) => {
      unwrapApiResponse(await addAccountToBuilding(buildingId, accountId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-building", selectedBuildingId] });
      setShowAddOwnerModal(false);
    },
  });

  const removeOwnerMutation = useMutation({
    mutationFn: async ({ buildingId, accountId }: { buildingId: number; accountId: number }) => {
      unwrapApiResponse(await removeAccountFromBuilding(buildingId, accountId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-building", selectedBuildingId] });
      setPendingRemoveOwner(null);
    },
  });

  const updateAddressMutation = useMutation({
    mutationFn: async ({
      buildingId,
      street,
      city,
      postcode,
      country,
    }: {
      buildingId: number;
      street: string;
      city: string;
      postcode: string;
      country: string;
    }) => {
      unwrapApiResponse(await updateBuildingAddress(buildingId, { street, city, postcode, country }));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-building", selectedBuildingId] });
      queryClient.invalidateQueries({ queryKey: ["admin-buildings"] });
      setShowEditAddressModal(false);
    },
  });

  const deleteBuildingMutation = useMutation({
    mutationFn: async (buildingId: number) => {
      unwrapApiResponse(await deleteBuilding(buildingId));
      return buildingId;
    },
    onSuccess: (deletedBuildingId) => {
      queryClient.invalidateQueries({ queryKey: ["admin-buildings"] });
      queryClient.removeQueries({ queryKey: ["admin-building", deletedBuildingId], exact: true });
      setShowDeleteBuildingModal(false);
      setDeleteConfirmationValue("");
      onSelectedBuildingIdChange(null);
    },
  });

  const loadedBuildings = buildingsQuery.data?.pages.flatMap((page) => page.result?.items ?? []) ?? [];
  const buildingDetails = buildingDetailsQuery.data?.result;
  const deleteConfirmationToken = buildingDetails ? `DELETE ${buildingDetails.id}` : "";
  const deleteBuildingErrorMessage = getMutationErrorMessage(deleteBuildingMutation.error, "Failed to delete building");

  const closeDeleteBuildingModal = () => {
    if (deleteBuildingMutation.isPending) {
      return;
    }

    deleteBuildingMutation.reset();
    setDeleteConfirmationValue("");
    setShowDeleteBuildingModal(false);
  };

  // If selected building is not in the loaded list, prepend it from the details query
  const selectedBuildingInList = selectedBuildingId ? loadedBuildings.some((b) => b.id === selectedBuildingId) : true;
  const buildings =
    !selectedBuildingInList && buildingDetails
      ? [
          {
            id: buildingDetails.id,
            street: buildingDetails.street,
            city: buildingDetails.city,
            postcode: buildingDetails.postcode,
          },
          ...loadedBuildings.filter((b) => b.id !== buildingDetails.id),
        ]
      : loadedBuildings;

  // Scroll to selected row when data is loaded and row is available
  useEffect(() => {
    if (selectedBuildingId && (buildings.length > 0 || buildingDetails)) {
      requestAnimationFrame(() => {
        if (selectedRowRef.current) {
          selectedRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    }
  }, [selectedBuildingId, buildings.length, buildingDetails]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const SortIndicator = ({ column }: { column: SortColumn }) => (
    <span className="ml-1">{sortColumn === column ? (sortDirection === "asc" ? "▲" : "▼") : ""}</span>
  );

  const renderExpandedRow = (buildingId: number) => {
    if (selectedBuildingId !== buildingId) {
      return null;
    }

    return (
      <tr key={`details-${buildingId}`}>
        <td colSpan={4} className="p-0 border-b">
          <div className="bg-gray-50 p-4 border-t">
            {buildingDetailsQuery.isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner className="w-8 h-8" />
              </div>
            ) : buildingDetails ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div>
                  <h3 className="text-lg font-bold mb-3">Building Details</h3>
                  <dl className="space-y-2">
                    <div className="flex gap-2">
                      <dt className="font-medium">ID:</dt>
                      <dd>{buildingDetails.id}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="font-medium">Address:</dt>
                      <dd>
                        {buildingDetails.street}, {buildingDetails.city}
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="font-medium">Postcode:</dt>
                      <dd>{buildingDetails.postcode}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="font-medium">Country:</dt>
                      <dd>{buildingDetails.country}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="font-medium">Households:</dt>
                      <dd>{buildingDetails.householdsCount}</dd>
                    </div>
                  </dl>
                  <div className="mt-4 flex flex-wrap gap-2 border-t pt-4">
                    <Button size="sm" onClick={() => setShowEditAddressModal(true)}>
                      Edit Address
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => {
                        deleteBuildingMutation.reset();
                        setDeleteConfirmationValue("");
                        setShowDeleteBuildingModal(true);
                      }}
                    >
                      Delete Building
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onSelectedBuildingIdChange(null)}>
                      Close
                    </Button>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-bold">Development</h3>
                    <Button size="sm" onClick={() => setShowChangeDevelopmentModal(true)}>
                      Change
                    </Button>
                  </div>
                  {buildingDetails.development ? (
                    <div
                      className="p-2 bg-white rounded border cursor-pointer hover:bg-gray-100"
                      onClick={() => onSelectDevelopment(buildingDetails.development!.id)}
                    >
                      <div className="font-medium">{buildingDetails.development.name}</div>
                      <div className="text-sm text-gray-500">ID: {buildingDetails.development.id}</div>
                    </div>
                  ) : (
                    <div className="text-gray-500 text-center py-2">No development assigned</div>
                  )}
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-bold">Owner Accounts</h3>
                    <Button size="sm" onClick={() => setShowAddOwnerModal(true)}>
                      Add Owner
                    </Button>
                  </div>
                  <ul className="space-y-2 max-h-64 overflow-y-auto">
                    {buildingDetails.ownerAccounts.map((account) => (
                      <li key={account.id} className="flex justify-between items-center p-2 bg-white rounded border">
                        <div
                          onClick={() => onSelectAccount(account.id)}
                          className="cursor-pointer hover:text-dobu-orange transition-colors"
                        >
                          <div className="font-medium">{account.name}</div>
                          <div className="text-sm text-gray-500">
                            {account.type} - ID: {account.id}
                          </div>
                        </div>
                        <Button
                          onClick={() => setPendingRemoveOwner({ accountId: account.id, name: account.name })}
                          disabled={removeOwnerMutation.isPending}
                          variant="danger"
                          size="sm"
                        >
                          Remove
                        </Button>
                      </li>
                    ))}
                    {buildingDetails.ownerAccounts.length === 0 && (
                      <li className="text-gray-500 text-center py-2">No owner accounts</li>
                    )}
                  </ul>
                </div>
              </div>
            ) : (
              <div className="text-red-500">Failed to load building details</div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div>
      <div className="flex gap-4 mb-4">
        <Input
          placeholder="Search buildings..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mt-0 flex-1 rounded-lg px-4 py-2"
        />
      </div>

      {buildingsQuery.isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner className="w-8 h-8" />
        </div>
      ) : buildingsQuery.isError ? (
        <div className="text-red-500 py-4">Failed to load buildings</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-200"
                    onClick={() => handleSort("id")}
                  >
                    ID
                    <SortIndicator column="id" />
                  </th>
                  <th
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-200"
                    onClick={() => handleSort("street")}
                  >
                    Street
                    <SortIndicator column="street" />
                  </th>
                  <th
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-200"
                    onClick={() => handleSort("city")}
                  >
                    City
                    <SortIndicator column="city" />
                  </th>
                  <th
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-200"
                    onClick={() => handleSort("postcode")}
                  >
                    Postcode
                    <SortIndicator column="postcode" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {buildings.map((building) => (
                  <React.Fragment key={building.id}>
                    <tr
                      ref={selectedBuildingId === building.id ? selectedRowRef : null}
                      className={`border-b cursor-pointer hover:bg-gray-50 ${
                        selectedBuildingId === building.id ? "bg-blue-50" : ""
                      }`}
                      onClick={() =>
                        onSelectedBuildingIdChange(selectedBuildingId === building.id ? null : building.id)
                      }
                    >
                      <td className="p-3">{building.id}</td>
                      <td className="p-3">{building.street}</td>
                      <td className="p-3">{building.city}</td>
                      <td className="p-3">{building.postcode}</td>
                    </tr>
                    {renderExpandedRow(building.id)}
                  </React.Fragment>
                ))}
                {buildings.length === 0 && (
                  <tr>
                    <td colSpan={4} className="p-4 text-center text-gray-500">
                      No buildings found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div ref={loadMoreRef} className="flex justify-center py-4">
            {buildingsQuery.isFetchingNextPage && <Spinner className="w-6 h-6" />}
          </div>
        </>
      )}

      {showChangeDevelopmentModal && selectedBuildingId && (
        <ChangeDevelopmentModal
          currentDevelopmentId={buildingDetails?.development?.id ?? null}
          onClose={() => setShowChangeDevelopmentModal(false)}
          onSelect={(developmentId) =>
            updateDevelopmentMutation.mutate({ buildingId: selectedBuildingId, developmentId })
          }
          isLoading={updateDevelopmentMutation.isPending}
        />
      )}

      {showAddOwnerModal && selectedBuildingId && (
        <AddOwnerAccountModal
          existingAccountIds={buildingDetails?.ownerAccounts.map((a) => a.id) ?? []}
          onClose={() => setShowAddOwnerModal(false)}
          onAdd={(accountId) => addOwnerMutation.mutate({ buildingId: selectedBuildingId, accountId })}
          isLoading={addOwnerMutation.isPending}
        />
      )}

      {showEditAddressModal && selectedBuildingId && buildingDetails && (
        <EditAddressModal
          currentAddress={{
            street: buildingDetails.street,
            city: buildingDetails.city,
            postcode: buildingDetails.postcode,
            country: buildingDetails.country,
          }}
          onClose={() => setShowEditAddressModal(false)}
          onSave={(address) =>
            updateAddressMutation.mutate({
              buildingId: selectedBuildingId,
              ...address,
            })
          }
          isLoading={updateAddressMutation.isPending}
        />
      )}

      {buildingDetails && (
        <ConfirmationDialog
          isOpen={showDeleteBuildingModal}
          onCancel={closeDeleteBuildingModal}
          onConfirm={() => {
            if (!deleteBuildingMutation.isPending && deleteConfirmationValue === deleteConfirmationToken) {
              deleteBuildingMutation.mutate(buildingDetails.id);
            }
          }}
          title={`Delete Building ${buildingDetails.id}`}
          message="This permanently deletes the building and every household beneath it. This action cannot be undone."
          confirmText="Permanently Delete Building"
          cancelText="Cancel"
          confirmDisabled={deleteConfirmationValue !== deleteConfirmationToken}
          loading={deleteBuildingMutation.isPending}
        >
          <div className="space-y-4 text-left">
            <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-900">
              <p className="font-semibold">Review the target carefully before continuing.</p>
              <dl className="mt-3 space-y-1 text-sm">
                <div className="flex gap-2">
                  <dt className="font-medium">Building ID:</dt>
                  <dd>{buildingDetails.id}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="font-medium">Address:</dt>
                  <dd>{formatBuildingAddress(buildingDetails)}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="font-medium">Households:</dt>
                  <dd>{buildingDetails.householdsCount}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="font-medium">Owner accounts:</dt>
                  <dd>{buildingDetails.ownerAccounts.length}</dd>
                </div>
              </dl>
            </div>

            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700">
              <li>Every household inside this building will be deleted first.</li>
              <li>Owners will lose access to the building and its household data.</li>
              <li>Support history, documents, and related records may be destroyed permanently.</li>
            </ul>

            <Field hasError={deleteBuildingMutation.isError}>
              <Field.Label>Type the exact phrase below to unlock deletion</Field.Label>
              <Input
                value={deleteConfirmationValue}
                onChange={(event) => setDeleteConfirmationValue(event.target.value)}
                placeholder={deleteConfirmationToken}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
              />
              <p className="mt-2 text-xs text-gray-500">
                Required text:{" "}
                <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-gray-900">
                  {deleteConfirmationToken}
                </span>
              </p>
              <Field.Error>{deleteBuildingErrorMessage}</Field.Error>
            </Field>
          </div>
        </ConfirmationDialog>
      )}

      <ConfirmationDialog
        isOpen={pendingRemoveOwner !== null}
        onCancel={() => setPendingRemoveOwner(null)}
        onConfirm={() => {
          if (pendingRemoveOwner && selectedBuildingId) {
            removeOwnerMutation.mutate({
              buildingId: selectedBuildingId,
              accountId: pendingRemoveOwner.accountId,
            });
          }
        }}
        title="Remove Owner"
        message={`Are you sure you want to remove "${pendingRemoveOwner?.name}" as an owner of this building?`}
        confirmText="Remove"
        cancelText="Cancel"
        loading={removeOwnerMutation.isPending}
      />
    </div>
  );
}

function unwrapApiResponse<T>(response: ApiResponse<T>): T {
  if (!response.success) {
    throw new ApiError(response.message);
  }

  return response.result;
}

function getMutationErrorMessage(error: unknown, fallback: string): string | null {
  if (!error) {
    return null;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

function formatBuildingAddress(building: { street: string; city: string; postcode: string; country: string }): string {
  return [building.street, building.city, building.postcode, building.country].filter(Boolean).join(", ");
}

function ChangeDevelopmentModal({
  currentDevelopmentId,
  onClose,
  onSelect,
  isLoading,
}: {
  currentDevelopmentId: number | null;
  onClose: () => void;
  onSelect: (developmentId: number | null) => void;
  isLoading: boolean;
}) {
  const [search, setSearch] = useState("");
  const [selectedDevelopment, setSelectedDevelopment] = useState<AdminDevelopment | null>(null);

  const developmentsQuery = useQuery({
    queryKey: ["admin-developments-for-select", search],
    queryFn: () => listDevelopments(search || undefined, 0, 10),
    enabled: search.length >= 2,
  });

  const availableDevelopments = (developmentsQuery.data?.result?.items ?? []).filter(
    (d) => d.id !== currentDevelopmentId,
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-96 max-w-full">
        <h2 className="text-xl font-bold mb-4">Change Development</h2>
        <div className="space-y-4">
          <Field>
            <Field.Label>Search Development</Field.Label>
            <Input
              placeholder="Type at least 2 characters..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedDevelopment(null);
              }}
            />
          </Field>

          {developmentsQuery.isLoading && (
            <div className="flex justify-center py-2">
              <Spinner className="w-6 h-6" />
            </div>
          )}

          {availableDevelopments.length > 0 && (
            <ul className="max-h-48 overflow-y-auto border rounded">
              {availableDevelopments.map((dev) => (
                <li
                  key={dev.id}
                  className={`p-2 cursor-pointer hover:bg-gray-100 ${selectedDevelopment?.id === dev.id ? "bg-blue-50" : ""}`}
                  onClick={() => setSelectedDevelopment(dev)}
                >
                  <div className="font-medium">{dev.name}</div>
                  <div className="text-sm text-gray-500">ID: {dev.id}</div>
                </li>
              ))}
            </ul>
          )}

          {search.length >= 2 && !developmentsQuery.isLoading && availableDevelopments.length === 0 && (
            <div className="text-gray-500 text-center py-2">No developments found</div>
          )}

          <div className="flex justify-between gap-2 pt-4">
            <Button
              onClick={() => onSelect(null)}
              disabled={isLoading || currentDevelopmentId === null}
              variant="danger"
            >
              Remove from Development
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                onClick={() => selectedDevelopment && onSelect(selectedDevelopment.id)}
                disabled={isLoading || !selectedDevelopment}
                loading={isLoading}
              >
                Select
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddOwnerAccountModal({
  existingAccountIds,
  onClose,
  onAdd,
  isLoading,
}: {
  existingAccountIds: number[];
  onClose: () => void;
  onAdd: (accountId: number) => void;
  isLoading: boolean;
}) {
  const [search, setSearch] = useState("");
  const [selectedAccount, setSelectedAccount] = useState<AdminAccount | null>(null);

  const accountsQuery = useQuery({
    queryKey: ["admin-accounts-for-add", search],
    queryFn: () => listAccounts(search || undefined, 0, 10),
    enabled: search.length >= 2,
  });

  const availableAccounts = (accountsQuery.data?.result?.items ?? []).filter((a) => !existingAccountIds.includes(a.id));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-96 max-w-full">
        <h2 className="text-xl font-bold mb-4">Add Owner Account</h2>
        <div className="space-y-4">
          <Field>
            <Field.Label>Search Account</Field.Label>
            <Input
              placeholder="Type at least 2 characters..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedAccount(null);
              }}
            />
          </Field>

          {accountsQuery.isLoading && (
            <div className="flex justify-center py-2">
              <Spinner className="w-6 h-6" />
            </div>
          )}

          {availableAccounts.length > 0 && (
            <ul className="max-h-48 overflow-y-auto border rounded">
              {availableAccounts.map((account) => (
                <li
                  key={account.id}
                  className={`p-2 cursor-pointer hover:bg-gray-100 ${selectedAccount?.id === account.id ? "bg-blue-50" : ""}`}
                  onClick={() => setSelectedAccount(account)}
                >
                  <div className="font-medium">{account.name}</div>
                  <div className="text-sm text-gray-500">
                    {account.type} - ID: {account.id}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {search.length >= 2 && !accountsQuery.isLoading && availableAccounts.length === 0 && (
            <div className="text-gray-500 text-center py-2">No accounts found</div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={() => selectedAccount && onAdd(selectedAccount.id)}
              disabled={isLoading || !selectedAccount}
              loading={isLoading}
            >
              Add Owner
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditAddressModal({
  currentAddress,
  onClose,
  onSave,
  isLoading,
}: {
  currentAddress: { street: string; city: string; postcode: string; country: string };
  onClose: () => void;
  onSave: (address: { street: string; city: string; postcode: string; country: string }) => void;
  isLoading: boolean;
}) {
  const [street, setStreet] = useState(currentAddress.street);
  const [city, setCity] = useState(currentAddress.city);
  const [postcode, setPostcode] = useState(currentAddress.postcode);
  const [country, setCountry] = useState(currentAddress.country);

  const hasChanges =
    street !== currentAddress.street ||
    city !== currentAddress.city ||
    postcode !== currentAddress.postcode ||
    country !== currentAddress.country;

  const isValid = street.trim() && city.trim() && postcode.trim() && country.trim();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-96 max-w-full">
        <h2 className="text-xl font-bold mb-4">Edit Address</h2>
        <div className="space-y-4">
          <Field>
            <Field.Label>Street</Field.Label>
            <Input value={street} onChange={(e) => setStreet(e.target.value)} />
          </Field>
          <Field>
            <Field.Label>City</Field.Label>
            <Input value={city} onChange={(e) => setCity(e.target.value)} />
          </Field>
          <Field>
            <Field.Label>Postcode</Field.Label>
            <Input value={postcode} onChange={(e) => setPostcode(e.target.value)} />
          </Field>
          <Field>
            <Field.Label>Country</Field.Label>
            <Input value={country} onChange={(e) => setCountry(e.target.value)} />
          </Field>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                onSave({ street: street.trim(), city: city.trim(), postcode: postcode.trim(), country: country.trim() })
              }
              disabled={isLoading || !hasChanges || !isValid}
              loading={isLoading}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
